import './builtinBehaviors';
import './iconRegistry';
import '../adapters/vegaLiteAdapter';
import '../adapters/autkGrammarAdapter';

// ── Dynamic-package globals ────────────────────────────────────────────
//
// Packages declaring `manifest.behaviorScript` ship a pre-built
// `behaviors.js` that gets loaded via <script> injection at boot
// (see `packageRegistryBootstrap.loadPackageBehaviorScripts`). That
// bundle externalises React, ReactFlow, and a `registerBehavior`
// function — we expose them on `window` so the package bundle's
// `const React = window.React` (etc.) replaces resolve at runtime,
// distinct bundles share Curio's React instance (essential for
// rules-of-hooks), and the bundle's side-effect can register against
// the same behavior registry the built-ins use. A package's node reads an
// output with `window.curio.fetchData`, the reader the canvas uses.
//
// Pending registrations: if a package's <script> loads before this
// module runs (race during first paint), it pushes a callback into
// `__curioPendingPackages__`. We drain those here so registration
// completes deterministically.
import * as ReactNS from 'react';
import { getToken } from '../utils/authApi';
import * as ReactFlowNS from 'reactflow';
import { fetchData } from '../services/api';
import { registerBehavior } from './behaviorRegistry';

if (typeof window !== 'undefined') {
  const w = window as any;
  w.React = ReactNS;
  w.ReactFlow = ReactFlowNS;
  // ``backendUrl`` is the runtime resolution of the host's ``BACKEND_URL``
  // env var. Packages should read this instead of inlining
  // the env var at their own build time — that path bakes
  // the URL into the published catalog bundle and breaks for any
  // deployment that doesn't match the build host.
  w.curio = {
    ...(w.curio ?? {}),
    registerBehavior,
    // A getter, like getAuthToken below: resolved on every read, so a
    // package bundle sees the runtime value, never a string captured at load.
    get backendUrl() {
      return backendUrl();
    },
    // A getter, not a value: a package bundle evaluates once at load, long
    // before sign-in, so a captured string would be stale forever. Packages
    // whose backend needs to know WHO is calling (for the caller's own keys)
    // send this as a Bearer header.
    getAuthToken: () => getToken(),
    // The canvas's own reader of an output, so a package's node reads one as
    // a built-in node does: from what a dashboard page carries or a viewer's
    // run made, else on the open dataflow's route (services/dataflowReads.ts),
    // which reads an output the dataflow saved under any sign-in, else `/get`.
    fetchData,
  };
  const pending: Array<(c: { registerBehavior: typeof registerBehavior }) => void> =
    Array.isArray(w.__curioPendingPackages__) ? w.__curioPendingPackages__ : [];
  for (const fn of pending) {
    try {
      fn(w.curio);
    } catch (e) {
      // One bad package shouldn't take the whole boot down.
      console.error('[curio] pending package registration failed:', e);
    }
  }
  w.__curioPendingPackages__ = [];
}

export {
  registerNode,
  getNodeDescriptor,
  tryGetNodeDescriptor,
  getAllNodeTypes,
  getPaletteNodeTypes,
  modelTypeOfPackage,
  subscribeToRegistry,
} from './nodeRegistry';

export type { NodeTemplateId, NodeSource, NodePackageMeta } from './types';

export {
  registerGrammarAdapter,
  getGrammarAdapter,
  getAllGrammarAdapters,
} from './grammarAdapter';

export {
  registerBehavior,
  getBehavior,
} from './behaviorRegistry';

export type {
  NodeDescriptor,
  PortDef,
  EditorType,
  NodeCategory,
  HandleDef,
  EditorConfig,
  ContainerConfig,
  NodeAdapter,
  NodeBehaviorHook,
  NodeBehaviorData,
  NodeBehaviorResult,
  UseNodeStateReturn,
} from './types';

export type {
  GrammarAdapter,
} from './grammarAdapter';
import { backendUrl } from '../utils/backendUrl';
